import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Grid,
  FormControlLabel,
  Checkbox,
  TextField
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CategoriesData from '../data/CategoriesInf';
import {
  listGiraInfantilEtapasByGira,
  getEventualCupoFromEtapaDoc,
  getEventualEligibilityInfantil
} from '../../lib/appwrite';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 600 },
  maxWidth: 600,
  maxHeight: { xs: '90vh', sm: '80vh' },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  overflow: 'auto'
};

// Parse YYYY-MM-DD (or ISO) as local midnight to avoid UTC day shifts (e.g. México UTC-6)
const parseAsLocalDate = (dateStr) => {
  if (!dateStr) return new Date();
  const [year, month, day] = String(dateStr).split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return new Date(dateStr);
  return new Date(year, month - 1, day);
};

// Edad cumplida a la fecha de referencia (nacional de la gira)
const calculateAge = (birthDate, tournamentDate = null) => {
  const referenceDate = tournamentDate ? parseAsLocalDate(tournamentDate) : new Date();
  const birth = parseAsLocalDate(birthDate);
  let age = referenceDate.getFullYear() - birth.getFullYear();
  const monthDiff = referenceDate.getMonth() - birth.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && referenceDate.getDate() < birth.getDate())) {
    age--;
  }

  return age;
};

const GiraInfRegistration = ({ 
  open, 
  onClose, 
  selectedGira,
  registeredChildren,
  onSave,
  userPadcodigo,
  currentRegistrations,
  isSaving = false
}) => {
  const [selectedChild, setSelectedChild] = useState('');
  const [isCIJ, setIsCIJ] = useState(false);
  const [isWAGR, setIsWAGR] = useState(false);
  const [isEventual, setIsEventual] = useState(false);
  const [division, setDivision] = useState(1); // 0 menores de 10; 1 Premier; 2 Campeonato
  const [categories, setCategories] = useState([]);
  const [localIsSaving, setLocalIsSaving] = useState(false);
  const [appwriteEtapas, setAppwriteEtapas] = useState([]);
  const [eventualHasCupo, setEventualHasCupo] = useState(true);
  const [eventualEstadoOk, setEventualEstadoOk] = useState(true);
  const [eventualCheckLoading, setEventualCheckLoading] = useState(false);
  const [eventualBlockReason, setEventualBlockReason] = useState('');

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await CategoriesData.fetch();
        if (!categoriesData.message) {
          setCategories(categoriesData);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };

    fetchCategories();
  }, []);

  // Etapas desde Appwrite (girainfantil) para cupo eventual
  useEffect(() => {
    if (!open || !selectedGira?.gira) {
      setAppwriteEtapas([]);
      return;
    }
    let cancelled = false;
    listGiraInfantilEtapasByGira(selectedGira.gira)
      .then((docs) => {
        if (!cancelled) setAppwriteEtapas(Array.isArray(docs) ? docs : []);
      })
      .catch(() => {
        if (!cancelled) setAppwriteEtapas([]);
      });
    return () => { cancelled = true; };
  }, [open, selectedGira?.gira]);

  // Reset local saving state when parent isSaving changes
  useEffect(() => {
    if (!isSaving) {
      setLocalIsSaving(false);
    }
  }, [isSaving]);

  // Próxima etapa vigente: SQL por fechas; si no hay, Appwrite (menor Etapa con cupo, o menor Etapa)
  const getNextUpcomingEtapaCodigo = (etapas) => {
    if (!etapas || !Array.isArray(etapas) || etapas.length === 0) return 0;
    const fecha = new Date();
    const sortedEtapas = [...etapas].sort((a, b) =>
      new Date(a.etafinicio) - new Date(b.etafinicio)
    );
    const nextEtapa = sortedEtapas.find(etapa =>
      new Date(etapa.etafinicio) > fecha
    );
    const fallback = sortedEtapas[sortedEtapas.length - 1];
    const etapa = nextEtapa || fallback;
    return etapa?.etacodigo ?? etapa?.Etacodigo ?? 0;
  };

  const getEtapaFromAppwrite = (docs) => {
    if (!docs || !Array.isArray(docs) || docs.length === 0) return 0;
    const sorted = [...docs].sort((a, b) => Number(a.Etapa) - Number(b.Etapa));
    const withCupo = sorted.find((d) => getEventualCupoFromEtapaDoc(d).disponible);
    const chosen = withCupo || sorted[0];
    return Number(chosen?.Etapa) || 0;
  };

  const eventualEtapa =
    getNextUpcomingEtapaCodigo(selectedGira?.etapas) ||
    getEtapaFromAppwrite(appwriteEtapas);
  const isEventualBlocked = isEventual && (eventualCheckLoading || !eventualHasCupo || !eventualEstadoOk);

  // Cupo Appwrite + estado del club (ClubxEstado) vs estado de la etapa
  useEffect(() => {
    if (!isEventual) {
      setEventualHasCupo(true);
      setEventualEstadoOk(true);
      setEventualBlockReason('');
      setEventualCheckLoading(false);
      return;
    }
    if (!selectedGira?.gira || !eventualEtapa) {
      setEventualHasCupo(false);
      setEventualEstadoOk(false);
      setEventualBlockReason('No hay etapa vigente para eventual.');
      return;
    }
    const child = selectedChild !== ''
      ? (registeredChildren || []).find((c) => Number(c.hicodigo) === Number(selectedChild))
      : null;
    if (!child) {
      setEventualHasCupo(true);
      setEventualEstadoOk(false);
      setEventualBlockReason('Selecciona un jugador para validar eventual.');
      setEventualCheckLoading(false);
      return;
    }

    let cancelled = false;
    setEventualCheckLoading(true);
    getEventualEligibilityInfantil(selectedGira.gira, eventualEtapa, child.club)
      .then((result) => {
        if (cancelled) return;
        setEventualHasCupo(result.cupoOk === true);
        setEventualEstadoOk(result.estadoOk === true);
        if (!result.estadoOk) {
          setEventualBlockReason(
            'El jugador no tiene permitido inscribirse como eventual en esta etapa.'
          );
        } else if (!result.cupoOk) {
          setEventualBlockReason('No hay lugares de eventuales disponibles para esta etapa.');
        } else {
          setEventualBlockReason('');
        }
      })
      .catch(() => {
        if (cancelled) return;
        setEventualHasCupo(false);
        setEventualEstadoOk(false);
        setEventualBlockReason('No se pudo validar el cupo o el estado para eventual.');
      })
      .finally(() => {
        if (!cancelled) setEventualCheckLoading(false);
      });
    return () => { cancelled = true; };
  }, [isEventual, selectedGira?.gira, eventualEtapa, selectedChild, registeredChildren]);

  // Bloquea si tiene gira completa (insetapa 0) o ya está inscrito en la etapa vigente
  const isPlayerAlreadyRegistered = (childHicodigo) => {
    if (!currentRegistrations || !Array.isArray(currentRegistrations) || !selectedGira) return false;
    const vigenteEtapa = eventualEtapa || getNextUpcomingEtapaCodigo(selectedGira.etapas);
    return currentRegistrations.some(reg =>
      Number(reg.insgira) === Number(selectedGira.gira) &&
      Number(reg.inshijo) === Number(childHicodigo) &&
      (Number(reg.insetapa) === 0 || Number(reg.insetapa) === Number(vigenteEtapa))
    );
  };

  // Filter out already registered players
  const availableChildren = (registeredChildren && Array.isArray(registeredChildren)) 
    ? registeredChildren.filter(child => !isPlayerAlreadyRegistered(child.hicodigo))
    : [];

  const handleChildChange = (event) => {
    
    setSelectedChild(event.target.value);
    
    // If a child is selected, check their age and set division accordingly
    if (event.target.value !== '') {
      const selectedChildData = availableChildren.find(child => child.hicodigo === event.target.value);
      // Misma referencia que la categoría: fecha nacional de la gira
      const provisionalAge = selectedChildData
        ? calculateAge(selectedChildData.fnacimiento, selectedGira?.nacional)
        : null;
      
      // Default Premier; effect will force 0 if age < 10
      setDivision(1);
      
      // Uncheck WAGR if they're under 14
      if (provisionalAge && provisionalAge < 14 && isWAGR) {
        setIsWAGR(false);
        console.log('Unchecking WAGR for child under 14');
      }
    }
  };

  const handleCIJChange = (event) => {
    setIsCIJ(event.target.checked);
  };

  const handleWAGRChange = (event) => {
    setIsWAGR(event.target.checked);
  };

  const handleDivisionChange = (event) => {
    setDivision(event.target.value);
  };

  const handleEventualChange = (event) => {
    setIsEventual(event.target.checked);
    if (event.target.checked) {
      setIsCIJ(false);
      setIsWAGR(false);
    }
  };

  const selectedChildData = selectedChild !== '' ? availableChildren.find(child => child.hicodigo === selectedChild) : null;
  // Todas las divisiones usan la fecha nacional (p. ej. gira 16 → 25-03-27) para edad/categoría
  const ageReferenceDate = selectedGira?.nacional || selectedGira?.fin;
  const childAge = selectedChildData ? calculateAge(selectedChildData.fnacimiento, ageReferenceDate) : null;
  
  const getCategory = (age, rama) => {
    if (age == null || Number.isNaN(age) || !categories.length) return { id: null, name: '' };
    
    const matchingCategory = categories.find(cat => 
      age >= cat.emin && 
      age <= cat.emax && 
      cat.rama === rama
    );
    
    return matchingCategory ? { id: matchingCategory.id, name: matchingCategory.nombre } : { id: null, name: '' };
  };

  const childCategory = childAge != null && !Number.isNaN(childAge)
    ? getCategory(childAge, selectedChildData?.rama)
    : { id: null, name: '' };
  const canSelectDivision = childAge != null && childAge >= 10;

  // Menores de 10 → insmarcadivision = 0 automático (sin selector Premier/Campeonato)
  useEffect(() => {
    if (!selectedChild || childAge == null) return;
    if (!canSelectDivision) {
      if (division !== 0) setDivision(0);
    } else if (division === 0) {
      setDivision(1);
    }
  }, [selectedChild, childAge, canSelectDivision, division]);

  const handleSave = async () => {
    // Prevent multiple submissions using both local and prop state
    if (isSaving || localIsSaving || isEventualBlocked) {
      console.log('Save operation already in progress or eventual blocked, ignoring click');
      return;
    }
    
    if (!selectedChildData || !selectedGira || !userPadcodigo) return;

    const etapaValue = isEventual ? eventualEtapa : 0;
    if (isEventual && !etapaValue) {
      return;
    }

    if (isEventual) {
      try {
        const eligibility = await getEventualEligibilityInfantil(
          selectedGira.gira,
          etapaValue,
          selectedChildData.club
        );
        if (!eligibility.disponible) {
          setEventualHasCupo(eligibility.cupoOk === true);
          setEventualEstadoOk(eligibility.estadoOk === true);
          if (!eligibility.estadoOk) {
            setEventualBlockReason(
              'El club del jugador no corresponde al estado de esta etapa; no puede inscribirse como eventual.'
            );
          } else if (!eligibility.cupoOk) {
            setEventualBlockReason('No hay lugares de eventuales disponibles para esta etapa.');
          }
          return;
        }
      } catch {
        setEventualHasCupo(false);
        setEventualEstadoOk(false);
        setEventualBlockReason('No se pudo validar el cupo o el estado para eventual.');
        return;
      }
    }

    // Set local saving state immediately
    setLocalIsSaving(true);

    const registrationData = {
      giracodigo: selectedGira.gira,
      padcodigo: userPadcodigo,
      hicodigo: selectedChildData.hicodigo,
      hijugador: selectedChildData.hijugador,
      clubcodigo: selectedChildData.club,
      hinombre: `${selectedChildData.nombre?.trim()} ${selectedChildData.apellidoPaterno?.trim()} ${selectedChildData.apellidoMaterno?.trim()}`.trim(),
      isCIJ: isCIJ,
      isWAGR: isWAGR,
      isEventual: isEventual,
      etapa: etapaValue,
      division: canSelectDivision ? division : 0,
      categoria: childCategory.name,
      categoriaId: childCategory.id,
      edad: childAge
    };
    onSave(registrationData);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="gira-modal-title"
    >
      <Box sx={style}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography id="gira-modal-title" variant="h6" component="h2">
            Inscripción a Gira
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        
        {selectedGira && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Gira Information */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'center', sm: 'flex-start' },
              gap: 2,
              textAlign: { xs: 'center', sm: 'left' }
            }}>
              <Box
                component="img"
                src={selectedGira.foto}
                alt={`Logo ${selectedGira.nombre}`}
                sx={{
                  width: { xs: 80, sm: 100 },
                  height: { xs: 80, sm: 100 },
                  objectFit: 'contain',
                  borderRadius: 2,
                  flexShrink: 0,
                  display: { xs: 'none', sm: 'block' }
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {selectedGira.nombre}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Si desea adicionar CIJ o WAGR seleccionar la casilla correspondiente
                </Typography>
              </Box>
            </Box>

            {/* Registration Form */}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isEventual}
                      onChange={handleEventualChange}
                      color="primary"
                      disabled={!eventualEtapa}
                    />
                  }
                  label="Eventual (Etapa vigente unicamente)"
                />
                {isEventual && (
                  <Typography variant="body2" color={isEventualBlocked ? 'error' : 'text.secondary'} sx={{ mt: 0.5 }}>
                    {eventualCheckLoading
                      ? 'Validando cupos y estado...'
                      : (isEventualBlocked ? eventualBlockReason : '')}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Seleccionar Jugador</InputLabel>
                  <Select
                    value={selectedChild}
                    label="Seleccionar Jugador"
                    onChange={handleChildChange}
                    disabled={availableChildren.length === 0}
                  >
                                         {availableChildren.length > 0 ? (
                       availableChildren.map((child, index) => (
                         <MenuItem key={index} value={child.hicodigo}>
                           {`${child.nombre} ${child.apellidoPaterno} ${child.apellidoMaterno}`}
                         </MenuItem>
                       ))
                     ) : (
                      <MenuItem disabled>
                        Todos los jugadores ya están inscritos
                      </MenuItem>
                    )}
                  </Select>
                  {availableChildren.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Todos los jugadores registrados ya están inscritos en este torneo.
                    </Typography>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: 1, sm: 4 }
                  }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isCIJ}
                          onChange={handleCIJChange}
                          color="primary"
                          disabled={isEventual}
                        />
                      }
                      label="CIJ"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={isWAGR}
                          onChange={handleWAGRChange}
                          color="primary"
                          disabled={isEventual || (childAge && childAge < 14)}
                        />
                      }
                      label="WAGR"
                    />
                    {canSelectDivision && (
                      <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>División</InputLabel>
                        <Select
                          value={division === 0 ? 1 : division}
                          label="División"
                          onChange={handleDivisionChange}
                          disabled={isEventual}
                        >
                          <MenuItem value={1}>Premier</MenuItem>
                          <MenuItem value={2}>Campeonato</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  </Box>
                  <TextField
                    label="Categoría"
                    value={childCategory?.name || ''}
                    disabled
                    size="small"
                    sx={{ 
                      width: { xs: '100%', sm: '150px' },
                      minWidth: { xs: 'auto', sm: '150px' }
                    }}
                  />
                </Box>
              </Grid>
            </Grid>

            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'flex-end', 
              gap: 2, 
              mt: 2 
            }}>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  order: { xs: 2, sm: 1 }
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={selectedChild === '' || availableChildren.length === 0 || isSaving || localIsSaving || isEventualBlocked}
                sx={{ 
                  borderRadius: 2, 
                  textTransform: 'none',
                  order: { xs: 1, sm: 2 }
                }}
                // Additional protection against double clicks
                onMouseDown={(e) => {
                  if (isSaving || localIsSaving) {
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }}
              >
                {(isSaving || localIsSaving) ? 'Guardando...' : 'Confirmar Inscripción'}
              </Button>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  );
};

export default GiraInfRegistration; 